/**
 * SEO Target Matrix - helpers for programmatic landing pages.
 *
 * Data is stored in seoData.json so the build script (generateSeoPages.cjs)
 * can also read it via require() without ESM/CJS interop complexity.
 */
import seoData from "./seoData.json";

export const STL_NEIGHBORHOODS = seoData.STL_NEIGHBORHOODS;
export const SEO_SERVICES      = seoData.SEO_SERVICES;
export const SEO_TRADES        = seoData.SEO_TRADES;

// ─── Look-up helpers ──────────────────────────────────────────────────────────

export function findNeighborhoodByZip(zip) {
  return STL_NEIGHBORHOODS.find((n) => n.zip === zip) ?? null;
}

export function findNeighborhoodBySlug(slug) {
  return STL_NEIGHBORHOODS.find((n) => n.slug === slug) ?? null;
}

export function findServiceBySlug(slug) {
  return SEO_SERVICES.find((s) => s.slug === slug) ?? null;
}

export function findTradeBySlug(slug) {
  return SEO_TRADES.find((t) => t.slug === slug) ?? null;
}

// ─── URL helpers ──────────────────────────────────────────────────────────────

export function costPageUrl(serviceSlug, zip) {
  return `/cost/${serviceSlug}/${zip}`;
}

export function vendorPageUrl(tradeSlug, neighborhoodSlug) {
  return `/vendors/${tradeSlug}/${neighborhoodSlug}`;
}

// ─── Pre-message generator ───────────────────────────────────────────────────

export function buildPreMessage(service, neighborhood) {
  if (!service || !neighborhood) return "";
  return `I need help with ${service.name.toLowerCase()} in ${neighborhood.name} (${neighborhood.zip}).`;
}

// ─── Formatted price helpers ─────────────────────────────────────────────────

export function formatPrice(usd) {
  if (usd >= 1000) return `$${(usd / 1000).toFixed(usd % 1000 === 0 ? 0 : 1)}k`;
  return `$${usd}`;
}

export function priceRangeText(service) {
  if (!service) return "";
  return `${formatPrice(service.priceRangeLow)} – ${formatPrice(service.priceRangeHigh)}`;
}

// ─── SEO meta helpers ─────────────────────────────────────────────────────────

export function buildPageTitle(service, neighborhood) {
  if (!service || !neighborhood) return "Local Cost Guide | Clumse";
  return `Average Cost of ${service.name} in ${neighborhood.name} (${neighborhood.zip}) | Clumse`;
}

export function buildMetaDescription(service, neighborhood) {
  if (!service || !neighborhood) return "Get competing contractor quotes with Clumse.";
  return (
    `${service.name} cost in ${neighborhood.name}, ${neighborhood.region}: ` +
    `${formatPrice(service.priceRangeLow)}–${formatPrice(service.priceRangeHigh)} based on local benchmarks. ` +
    `Start your free diagnosis and get 3 vetted bids in ${neighborhood.zip}.`
  );
}

export function buildVendorPageTitle(trade, neighborhood) {
  if (!trade || !neighborhood) return "Find Local Contractors | Clumse";
  return `Find ${trade.displayTitle} in ${neighborhood.name}, ${neighborhood.region} | Clumse`;
}

export function buildVendorMetaDescription(trade, neighborhood) {
  if (!trade || !neighborhood) return "Get competing contractor quotes with Clumse.";
  return (
    `Compare ${trade.displayTitle.toLowerCase()} in ${neighborhood.name} (${neighborhood.zip}). ` +
    `Clumse matches you with vetted, licensed contractors - get 3 competing bids fast.`
  );
}

// ─── JSON-LD schema generators ────────────────────────────────────────────────

export function buildCostPageJsonLd(service, neighborhood) {
  if (!service || !neighborhood) return null;
  const pageUrl = `https://clumse.com${costPageUrl(service.slug, neighborhood.zip)}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://clumse.com/#clumse",
        "name": "Clumse",
        "url": "https://clumse.com",
        "logo": "https://clumse.com/clumse-logo.png",
        "description": "Clumse matches homeowners with vetted local contractors for accurate estimates and competitive bids.",
        "areaServed": {
          "@type": "GeoCircle",
          "geoMidpoint": {
            "@type": "GeoCoordinates",
            "postalCode": neighborhood.zip,
            "addressRegion": "MO",
            "addressCountry": "US"
          },
          "geoRadius": "50000"
        },
        "address": {
          "@type": "PostalAddress",
          "addressLocality": neighborhood.name,
          "addressRegion": "MO",
          "postalCode": neighborhood.zip,
          "addressCountry": "US"
        },
        "priceRange": `${formatPrice(service.priceRangeLow)}–${formatPrice(service.priceRangeHigh)}`
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        "mainEntity": service.faq.map((f) => ({
          "@type": "Question",
          "name": f.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": f.answer
          }
        }))
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://clumse.com" },
          { "@type": "ListItem", "position": 2, "name": "Cost Guides", "item": "https://clumse.com/cost" },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${service.name} in ${neighborhood.name}`,
            "item": pageUrl
          }
        ]
      }
    ]
  };
}

export function buildVendorPageJsonLd(trade, neighborhood) {
  if (!trade || !neighborhood) return null;
  const pageUrl = `https://clumse.com${vendorPageUrl(trade.slug, neighborhood.slug)}`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "LocalBusiness",
        "@id": "https://clumse.com/#clumse",
        "name": "Clumse",
        "url": "https://clumse.com",
        "description": `Find vetted ${trade.displayTitle.toLowerCase()} in ${neighborhood.name}, MO.`
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://clumse.com" },
          { "@type": "ListItem", "position": 2, "name": "Find Vendors", "item": "https://clumse.com/vendors" },
          {
            "@type": "ListItem",
            "position": 3,
            "name": `${trade.displayTitle} in ${neighborhood.name}`,
            "item": pageUrl
          }
        ]
      }
    ]
  };
}
